/**
 * Geo Location Tabs
 * -----------------
 * Opens the location tab closest to the visitor on tab sections that opt in with
 * data-geo-autoselect="true" (Location Bestsellers Tabs -> "Auto-select tab by
 * visitor location"). Everything else about the section is untouched: the tab
 * order, the markup and the click behaviour in content-tabs.js all stay as-is.
 *
 * Order of precedence on load:
 *   1. A tab the visitor picked themselves within the last 30 days (localStorage)
 *   2. A cached geo result from a previous visit (localStorage, 30 days) - applied
 *      synchronously, so repeat visitors never see the tab swap after paint
 *   3. A timezone guess for the cases timezone can answer on its own, applied
 *      immediately so Hawaii and non-US visitors do not sit on New York while the
 *      IP lookup is in flight
 *   4. IP geolocation (ipapi.co, falling back to geojs.io) for the precise city
 *
 * City -> studio mapping mirrors assets/location-selector.js on purpose, so the
 * header "Closest location" recommendation and this section always agree.
 * US visitors with no studio in their metro get the nearest studio by distance;
 * everyone else (non-US, or unknown) gets Virtual.
 *
 * No permission prompt is used - this is IP level only, never the browser
 * Geolocation API.
 */

(function () {
  'use strict';

  var CONFIG = {
    sectionSelector: '.content-tabs-section[data-geo-autoselect="true"]',
    geoApiUrl: 'https://ipapi.co/json/',
    /** HTTPS + CORS; used when ipapi is rate-limited or errors */
    geoFallbackUrl: 'https://get.geojs.io/v1/ip/geo.json',
    geoTimeoutMs: 5000,
    geoCacheKey: 'kraft_geo_tab_place_v1',
    choiceKey: 'kraft_geo_tab_choice_v1',
    ttlMs: 30 * 24 * 60 * 60 * 1000,
    fallbackHandle: 'virtual'
  };

  /* ------------------------------------------------------------------ *
   * Studio coordinates - used only for the "no studio in your metro"
   * fallback, where we hand a US visitor the nearest studio by distance.
   * ------------------------------------------------------------------ */
  var STUDIOS = [
    { handle: 'new-york', lat: 40.754, lon: -73.9925 },
    { handle: 'new-york-fidi', lat: 40.7043, lon: -74.0102 },
    { handle: 'san-francisco', lat: 37.7776, lon: -122.3949 },
    { handle: 'san-jose', lat: 37.3363, lon: -121.8946 },
    { handle: 'los-angeles', lat: 34.0522, lon: -118.2437 },
    { handle: 'irvine', lat: 33.65, lon: -117.743 },
    { handle: 'honolulu', lat: 21.3128, lon: -157.8598 },
    { handle: 'austin', lat: 30.2672, lon: -97.7431 }
  ];

  /*
   * City lists are ARRAYS and matched exactly - a substring search would match
   * 'ana' inside 'santa ana' and 'orange' inside 'garden grove'. All California
   * lists are gated on the region actually being California, because Newark and
   * Union City are real cities in both the Bay Area and New Jersey.
   * Kept in sync with assets/location-selector.js.
   */
  var LA_CITIES = ['los angeles','long beach','anaheim','santa ana','glendale','pasadena','torrance','pomona','fullerton','inglewood','el monte','downey','costa mesa','huntington beach','santa clarita','el cajon','simi valley','van nuys','norwalk','burbank','compton','south gate','carson','santa monica','whittier','alhambra','west covina','newport beach','mission viejo','buena park','cypress','la habra','lake forest','westminster','garden grove','orange','brea','placentia','yorba linda'];
  var SF_CITIES = ['san francisco','oakland','berkeley','daly city','south san francisco','alameda','san leandro','richmond'];
  var SAN_JOSE_CITIES = ['san jose','sunnyvale','santa clara','mountain view','palo alto','cupertino','milpitas','fremont','newark','union city','los altos','saratoga','campbell','gilroy'];
  var NY_METRO_STATES = ['NY', 'NJ', 'CT'];
  var AUSTIN_CITIES = ['austin','round rock','cedar park','georgetown','pflugerville','leander','san marcos','kyle','buda','lakeway','bee cave','manor','hutto','dripping springs','del valle','west lake hills'];

  /** US states + DC + territories, so we can tell "US, no studio nearby" from "abroad". */
  var US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','GU','VI','AS','MP'];

  function inList(list, value) {
    if (!value) return false;
    for (var i = 0; i < list.length; i++) {
      if (list[i] === value) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * Storage helpers - every access is wrapped, Safari private mode throws.
   * ------------------------------------------------------------------ */
  function readStore(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.handle || !parsed.ts) return null;
      if (Date.now() - parsed.ts > CONFIG.ttlMs) {
        window.localStorage.removeItem(key);
        return null;
      }
      return parsed.handle;
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, handle) {
    try {
      window.localStorage.setItem(key, JSON.stringify({ handle: handle, ts: Date.now() }));
    } catch (e) {}
  }

  /* ------------------------------------------------------------------ *
   * Tab discovery
   * ------------------------------------------------------------------ */

  /**
   * Derive a studio handle from a tab label, so no theme-editor setup is needed.
   * A block can override this with the "Location for auto-detect" block setting.
   */
  function handleFromLabel(label) {
    var s = String(label || '').toLowerCase();
    if (!s) return null;
    if (s.indexOf('virtual') !== -1 || s.indexOf('online') !== -1) return 'virtual';
    if (s.indexOf('fidi') !== -1 || s.indexOf('financial') !== -1) return 'new-york-fidi';
    if (s.indexOf('midtown') !== -1 || s.indexOf('new york') !== -1 || s.indexOf('nyc') !== -1) return 'new-york';
    if (s.indexOf('honolulu') !== -1 || s.indexOf('hawaii') !== -1 || s.indexOf('oahu') !== -1) return 'honolulu';
    if (s.indexOf('san francisco') !== -1 || s.indexOf('bay area') !== -1 || s.indexOf('soma') !== -1) return 'san-francisco';
    if (s.indexOf('san jose') !== -1 || s.indexOf('silicon valley') !== -1) return 'san-jose';
    if (s.indexOf('los angeles') !== -1 || s === 'la') return 'los-angeles';
    if (s.indexOf('irvine') !== -1 || s.indexOf('orange county') !== -1) return 'irvine';
    if (s.indexOf('austin') !== -1) return 'austin';
    return null;
  }

  /** [{ handle, button, panelId }] for one section, in DOM order. */
  function readTabs(section) {
    var buttons = section.querySelectorAll('.content-tabs-nav-item');
    var tabs = [];
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var explicit = (btn.getAttribute('data-geo-handle') || '').trim();
      var handle = explicit && explicit !== 'auto' ? explicit : handleFromLabel(btn.textContent);
      if (!handle) continue;
      tabs.push({ handle: handle, button: btn, panelId: btn.getAttribute('data-tab') });
    }
    return tabs;
  }

  function findTab(tabs, handle) {
    if (!handle) return null;
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].handle === handle) return tabs[i];
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Geo -> handle
   * ------------------------------------------------------------------ */

  /**
   * Rough bounding boxes to separate SF proper from the South Bay when the API
   * city is wrong (it often is for mobile carriers).
   */
  function bayAreaFromCoords(lat, lon) {
    if (typeof lat !== 'number' || typeof lon !== 'number') return null;
    if (isNaN(lat) || isNaN(lon)) return null;
    if (lat >= 37.65 && lon <= -122.35 && lon >= -122.55) return 'san-francisco';
    if (lat >= 37.2 && lat <= 37.5 && lon >= -122.15 && lon <= -121.7) return 'san-jose';
    if (lat >= 37.5 && lat <= 37.7 && lon <= -122.2) return 'san-francisco';
    return null;
  }

  /** Great-circle distance in km. */
  function distanceKm(lat1, lon1, lat2, lon2) {
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLon = (lon2 - lon1) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * When two studios are within this many km of each other from the visitor,
   * the distance difference is noise at that range - a Denver visitor is not
   * meaningfully closer to Irvine than to Los Angeles - so STUDIO_PRIORITY
   * decides instead.
   */
  var TIE_BREAK_KM = 200;
  var STUDIO_PRIORITY = ['new-york', 'los-angeles', 'san-francisco', 'honolulu', 'san-jose', 'irvine', 'austin'];

  function priorityOf(handle) {
    var i = STUDIO_PRIORITY.indexOf(handle);
    return i === -1 ? STUDIO_PRIORITY.length : i;
  }

  /**
   * Nearest studio that this section actually has a tab for. Virtual is never a
   * distance candidate, and the two New York studios collapse to Midtown - a
   * visitor from Boston should not be sent to FiDi over a 4 km difference.
   */
  function nearestAvailableStudio(lat, lon, tabs) {
    if (typeof lat !== 'number' || typeof lon !== 'number' || isNaN(lat) || isNaN(lon)) return null;

    // Keep the shortest distance per handle - Midtown and FiDi both answer to
    // 'new-york', so the same handle can come up twice.
    var byHandle = {};
    for (var i = 0; i < STUDIOS.length; i++) {
      var studio = STUDIOS[i];
      var handle = studio.handle === 'new-york-fidi' ? 'new-york' : studio.handle;
      if (!findTab(tabs, handle)) continue;
      var d = distanceKm(lat, lon, studio.lat, studio.lon);
      if (byHandle[handle] == null || d < byHandle[handle]) byHandle[handle] = d;
    }

    var candidates = [];
    for (var handleKey in byHandle) {
      if (Object.prototype.hasOwnProperty.call(byHandle, handleKey)) {
        candidates.push({ handle: handleKey, dist: byHandle[handleKey] });
      }
    }
    if (!candidates.length) return null;

    candidates.sort(function (a, b) {
      return a.dist - b.dist;
    });

    var best = candidates[0];
    for (var j = 1; j < candidates.length; j++) {
      if (candidates[j].dist - best.dist > TIE_BREAK_KM) break;
      if (priorityOf(candidates[j].handle) < priorityOf(best.handle)) best = candidates[j];
    }
    return best.handle;
  }

  /** Normalize an ipapi.co / geojs.io payload, or null if it tells us nothing. */
  function normalizeGeoPayload(data) {
    if (!data || typeof data !== 'object' || data.error === true) return null;

    var city = data.city != null ? String(data.city).trim() : '';
    var region = data.region != null ? String(data.region).trim() : '';
    var regionCode = data.region_code != null ? String(data.region_code).trim()
      : (data.regionCode != null ? String(data.regionCode).trim() : '');
    var country = data.country_code != null ? String(data.country_code).trim()
      : (data.country != null ? String(data.country).trim() : '');

    var latRaw = data.latitude != null ? data.latitude : data.lat;
    var lonRaw = data.longitude != null ? data.longitude : data.lon;
    var latitude = latRaw != null && latRaw !== '' ? Number(latRaw) : null;
    var longitude = lonRaw != null && lonRaw !== '' ? Number(lonRaw) : null;
    if (latitude != null && isNaN(latitude)) latitude = null;
    if (longitude != null && isNaN(longitude)) longitude = null;

    if (!city && !region && !regionCode && !country && latitude == null && longitude == null) return null;
    return {
      city: city || null,
      region: region || null,
      regionCode: regionCode || null,
      country: country ? country.toUpperCase().slice(0, 2) : null,
      latitude: latitude,
      longitude: longitude
    };
  }

  /**
   * Map a normalized geo reading to a tab handle.
   * Metro rules first (same order as location-selector.js), then nearest studio
   * for US visitors we could not place, then Virtual.
   */
  function mapGeoToHandle(geo, tabs) {
    if (!geo) return CONFIG.fallbackHandle;

    var c = (geo.city || '').toLowerCase().trim();
    var r = (geo.region || '').toLowerCase().trim();
    var rc = (geo.regionCode || '').toUpperCase().trim();
    var lat = geo.latitude;
    var lon = geo.longitude;

    // Some APIs put the two-letter code in `region` and leave region_code empty.
    if (r.length === 2 && rc === '' && /^[a-z]{2}$/.test(r)) {
      rc = r.toUpperCase();
      r = '';
    }

    var isUS = geo.country === 'US' || inList(US_STATES, rc);

    if (rc === 'HI' || r === 'hawaii' || c.indexOf('honolulu') !== -1) return 'honolulu';

    // New York metro first, and by state. Newark and Union City are the reason:
    // both are Bay Area cities AND New Jersey cities, so a NJ visitor has to be
    // claimed here before the California lists get a chance at them.
    if (
      inList(NY_METRO_STATES, rc) ||
      r === 'new york' || r === 'new jersey' || r === 'connecticut' ||
      c.indexOf('new york') !== -1 ||
      c === 'brooklyn' || c === 'manhattan' || c === 'queens' ||
      c === 'bronx' || c === 'staten island'
    ) {
      return 'new-york';
    }

    if (rc === 'TX' || r === 'texas' || inList(AUSTIN_CITIES, c)) {
      if (findTab(tabs, 'austin')) return 'austin';
    }

    var isCalifornia = rc === 'CA' || r === 'california';
    if (isCalifornia) {
      if (c === 'irvine') return 'irvine';
      if (inList(LA_CITIES, c)) return 'los-angeles';
      if (r.indexOf('orange') !== -1 && c !== 'irvine') return 'los-angeles';
      if (c.indexOf('los angeles') !== -1 || r.indexOf('los angeles') !== -1) return 'los-angeles';

      var fromCoords = bayAreaFromCoords(lat, lon);
      if (fromCoords) return fromCoords;

      if (
        c.indexOf('san francisco') !== -1 ||
        inList(SF_CITIES, c) ||
        (r.indexOf('san francisco') !== -1 && !inList(SAN_JOSE_CITIES, c))
      ) {
        return 'san-francisco';
      }
      if (inList(SAN_JOSE_CITIES, c) || c.indexOf('san jose') !== -1) return 'san-jose';
    }

    // In the US but not in one of our metros: give them the closest studio.
    if (isUS) {
      var nearest = nearestAvailableStudio(lat, lon, tabs);
      if (nearest) return nearest;
    }

    // Abroad, or we could not place them at all.
    return CONFIG.fallbackHandle;
  }

  /* ------------------------------------------------------------------ *
   * Timezone pre-pass - only the calls timezone can make on its own.
   * America/Los_Angeles covers SF, San Jose, LA and Irvine, so it is
   * deliberately not answered here; those wait for the IP lookup.
   * ------------------------------------------------------------------ */
  function handleFromTimezone() {
    var tz = '';
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return null;
    }
    if (!tz) return null;
    if (tz === 'Pacific/Honolulu') return 'honolulu';
    // Anything outside the Americas has no studio within reach.
    if (tz.indexOf('America/') !== 0 && tz.indexOf('Pacific/') !== 0 && tz !== 'US/Hawaii') {
      return CONFIG.fallbackHandle;
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Fetching
   * ------------------------------------------------------------------ */
  function fetchGeoOnce(url) {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeout = setTimeout(function () {
      if (controller) controller.abort();
    }, CONFIG.geoTimeoutMs);

    return fetch(url, controller ? { signal: controller.signal } : {})
      .then(function (res) {
        return res.json().then(function (data) {
          clearTimeout(timeout);
          if (!res.ok) return null;
          return normalizeGeoPayload(data);
        }).catch(function () {
          clearTimeout(timeout);
          return null;
        });
      })
      .catch(function () {
        clearTimeout(timeout);
        return null;
      });
  }

  function fetchGeo() {
    return fetchGeoOnce(CONFIG.geoApiUrl).then(function (primary) {
      if (primary) return primary;
      return fetchGeoOnce(CONFIG.geoFallbackUrl);
    });
  }

  /* ------------------------------------------------------------------ *
   * Applying a tab
   * ------------------------------------------------------------------ */

  /**
   * Same DOM work as ContentTabs.switchTab in content-tabs.js, done directly so
   * this never depends on that script having initialised first. Classes are the
   * only state content-tabs.js keeps, so the two stay in sync.
   */
  function activateTab(section, tab, source) {
    if (!tab || !tab.button || tab.button.classList.contains('active')) return false;

    var buttons = section.querySelectorAll('.content-tabs-nav-item');
    var panels = section.querySelectorAll('.content-tabs-panel');
    var i;

    for (i = 0; i < buttons.length; i++) {
      buttons[i].classList.remove('active');
      buttons[i].setAttribute('aria-selected', 'false');
    }
    for (i = 0; i < panels.length; i++) {
      panels[i].classList.remove('active');
    }

    tab.button.classList.add('active');
    tab.button.setAttribute('aria-selected', 'true');

    var panel = tab.panelId ? section.querySelector('#' + tab.panelId) : null;
    if (panel) panel.classList.add('active');

    // Bring the tab into view inside the strip only - never scroll the page.
    var nav = section.querySelector('.content-tabs-nav');
    if (nav && nav.scrollWidth > nav.clientWidth) {
      requestAnimationFrame(function () {
        var navRect = nav.getBoundingClientRect();
        var itemRect = tab.button.getBoundingClientRect();
        nav.scrollTo({ left: nav.scrollLeft + (itemRect.left - navRect.left), behavior: 'auto' });
      });
    }

    section.setAttribute('data-geo-selected', tab.handle);

    try {
      section.dispatchEvent(new CustomEvent('kraft:geo-tab-selected', {
        bubbles: true,
        detail: { handle: tab.handle, source: source }
      }));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: 'geo_tab_selected', geo_tab: tab.handle, geo_tab_source: source });
      }
    } catch (e) {}

    return true;
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */
  function initSection(section) {
    if (section.getAttribute('data-geo-init') === 'true') return;
    section.setAttribute('data-geo-init', 'true');

    var tabs = readTabs(section);
    if (tabs.length < 2) return;

    // Remember a tab the visitor picks themselves - it outranks geo next time.
    section.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('.content-tabs-nav-item') : null;
      if (!btn || !section.contains(btn)) return;
      var explicit = (btn.getAttribute('data-geo-handle') || '').trim();
      var handle = explicit && explicit !== 'auto' ? explicit : handleFromLabel(btn.textContent);
      if (handle) writeStore(CONFIG.choiceKey, handle);
    });

    // 1. Their own last choice.
    var chosen = findTab(tabs, readStore(CONFIG.choiceKey));
    if (chosen) {
      activateTab(section, chosen, 'saved-choice');
      return;
    }

    // 2. Cached geo result - synchronous, so no post-paint swap on repeat visits.
    var cached = findTab(tabs, readStore(CONFIG.geoCacheKey));
    if (cached) {
      activateTab(section, cached, 'geo-cache');
      return;
    }

    // 3. What timezone can answer on its own, right now.
    var tzTab = findTab(tabs, handleFromTimezone());
    if (tzTab) activateTab(section, tzTab, 'timezone');

    // 4. Precise city over IP.
    fetchGeo().then(function (geo) {
      if (!geo) return;
      var handle = mapGeoToHandle(geo, tabs);
      writeStore(CONFIG.geoCacheKey, handle);
      var tab = findTab(tabs, handle);
      if (tab) activateTab(section, tab, 'geo-ip');
    });
  }

  function init() {
    // In the theme editor the merchant is clicking through tabs on purpose.
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
    var sections = document.querySelectorAll(CONFIG.sectionSelector);
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
